import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import './PostDetail.css';
import Sidebar from "./Sidebar";

function PostDetail() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [similarPosts, setSimilarPosts] = useState([]);
  const [toc, setToc] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      const docRef = doc(db, "posts", postId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const postData = { ...docSnap.data(), id: docSnap.id };
        setPost(postData);
        fetchSimilarPosts(postData.tags);
        generateToc(postData.content);
      } else {
        console.log("No such document!");
      }
    };

    const fetchSimilarPosts = async (tags) => {
      if (!tags || tags.length === 0) {
        setSimilarPosts([]);
        return;
      }

      const q = query(collection(db, "posts"), where("tags", "array-contains-any", tags));
      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => p.id !== postId);

      setSimilarPosts(posts);
    };

    const generateToc = (content) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      const headings = doc.querySelectorAll('h2, h3'); // Add more heading levels if needed

      const tocItems = Array.from(headings).map((heading, index) => {
        const uniqueId = `heading-${index}`;
        heading.id = uniqueId; // Assign unique ID to each heading

        const truncateText = (text, wordLimit) => {
          const words = text.trim().split(/\s+/);
          if (words.length <= wordLimit) {
            return text;
          }
          return words.slice(0, wordLimit).join(' ') + '...';
        };

        return {
          id: heading.id,
          text: truncateText(heading.textContent, 5),
          level: heading.tagName.toLowerCase()
        };
      });

      setToc(tocItems);
    };

    fetchPost();
  }, [postId]);

  useEffect(() => {
    const handleHashChange = () => {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (post) {
      const handleHashChange = () => {
        const id = window.location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      handleHashChange();
    }
  }, [post]);

  const handlePrint = () => {
    window.print();
  };

  if (!post) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <Button variant="primary" onClick={() => navigate("/")}>Back to Home</Button>
      <Button variant="warning" onClick={handlePrint} className='pk-print-btn'>Print &#128424; </Button>

      <Container>
        <Row>
          <Col sm={8}>
            <div className="post-detail print-container">
              <h2>{post.title}</h2>
              <hr/>
              {post.imageUrl && (
                <img src={post.imageUrl} alt={post.title} className="post-image" />
              )}
              <div
                className="post-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
              <p><strong>Author:</strong> {post.author}</p>
              <p><strong>Tags:</strong> {post.tags ? post.tags.join(", ") : "No tags"}</p>
              <hr/>
              <div className="similar-posts">
                <h3>Similar Posts</h3>
                {similarPosts.length > 0 ? (
                  <ul>
                    {similarPosts.map(similarPost => (
                      <li key={similarPost.id} className="similar-post-item">
                        <Link to={`/post/${similarPost.id}`}>
                          {similarPost.imageUrl && (
                            <img src={similarPost.imageUrl} alt={similarPost.title} className="similar-post-image" />
                          )}
                          <span>{similarPost.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No similar posts found.</p>
                )}
              </div>
            </div>
          </Col>
          <Col sm={4}>
            <div className="toc border border-dark rounded">
              <h3>Table of Contents</h3>
              <ul>
                {toc.map(item => (
                  <li key={item.id} className={`toc-item toc-${item.level}`}>
                    <a href={`#${item.id}`}>{item.text}</a>
                  </li>
                ))}
              </ul>
            </div>
            <Sidebar />
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default PostDetail;
